import _get from 'lodash/get';
import urlResource from '../constants/UrlResources';
import { getCurrentRouteResourceName, getPageUrlInfoFromLocation } from '../../lib/location-helper';
import Page from '../../lib/page';

// Actions
import fetchTopicTree from './action-topic-list';
import getPopularQuestions from './action-popular-list';
import { fetchQueryParams } from '../../contactUs/actions/action-query-params';
import { clearSuggestions } from '../../contactUs/actions/action-auto-complete';
import getArticleListByTopic from './actions-article-list';
import fetchArticleContent from './action-article-content';
import { getHelpCenterAlerts, postChatBotEligibility } from '../../contactUs/actions/index';
import { resetToggleRelated } from '../../contactUs/actions/action-content-toggle';
import { resetBreadcrumb, updateBreadCrumbFromArticleContent } from './action-breadcrumb';
import fetchMachineArticle from './action-machine-article';
import { displayChatBot } from './action-chat-bot';
import getICFGuides from './action-allICF-guides';
import { fetchGuideContents } from './action-icf-guide';
import { icfClicked } from './action-icf-page';
import { wasChatBotClicked } from '../../lib/chat-bot-live-helper';
import { isNaaAlertEnabledInRC } from '../../lib/globalHelper';
import { fetchArticleSearchResult } from '../../contactUs/actions/action-article-search';

const experienceList = {
  personal: 'consumer',
  business: 'merchant',
  premier: 'consumer',
  technical: 'merchant', // TODO: update technical experience
};

const articleId = Page.getArticleIdLowerCase();
const isLoggedIn = Page.isLoggedIn();

// Compare with previous state to reduce data loading times
function isLocationOutOfSyncWithState(location, state) {
  return location.pathname !== state.location.pathname;
}

function isTopicTreeOutOfSyncWithState(location, state) {
  const pageInfo = getPageUrlInfoFromLocation(location);
  return pageInfo.experience !== state.topicList.experience;
}
function isPopularListOutOfSyncWithState(state) {
  return state.popular.articles && state.popular.articles.length <= 0;
}
function isTopicListOutOfSyncWithState(location, state) {
  const pageInfo = getPageUrlInfoFromLocation(location);
  return (pageInfo.experience !== state.location.experience || pageInfo.resource.id !== state.articleList.articleId);
}

function isArticleSyncOutOfWithState(location, state) {
  const pageInfo = getPageUrlInfoFromLocation(location);
  const stateArticleId = _get(state, 'article.article.articleId');
  return (pageInfo.experience !== state.location.experience || pageInfo.resource.id !== stateArticleId);
}

function isSearchSyncOutOfWithState(location, state) {
  const pageInfo = getPageUrlInfoFromLocation(location);
  return (pageInfo.experience !== state.location.experience || pageInfo.resource.searchTerm !== state.searchResult.q
    || pageInfo.resource.channelOption !== state.searchResult.channel);
}

function updateRouteState(location) {
  return {
    type: 'LOCATION_CHANGE',
    payload: { location },
  };
}

function getWebAlert({ dispatch, resource }) {
  // Check if its Home Page and pass `true` to return all the alerts.
  if (resource.name === urlResource.HOME) {
    dispatch(getHelpCenterAlerts(true));
    dispatch(resetToggleRelated());
    dispatch(resetBreadcrumb());
  } else {
    dispatch(getHelpCenterAlerts(false));
  }
}

/**
 * Post chat bot eligibility call for logged out to logged in when clicked on chat bot pill.
 * @param postChatBotParams
 * @returns {Function}
 */
function callPostChatBotEligibilityOnChatBotClick(postChatBotParams) {
  return (dispatch) => {
    dispatch(postChatBotEligibility({
      postChatBotResponse: postChatBotParams,
      showChat: displayChatBot,
      reject: displayChatBot,
    }));
  };
}

/**
 * Calling postChatBot Eligibility API for checking the eligibility to show/hide chatBot
 * @returns {Function}
 */
function postChatBotEligibilityResponse(location) {
  return (dispatch) => {
    // Get current route resource name.
    const currentRouteResource = getCurrentRouteResourceName(location);

    // Post chat bot eligibility API cal for Article page
    // If current route is article
    if (currentRouteResource === urlResource.ARTICLE) {
      const postChatBotParams = Page.getChatBotEligibilityParams(articleId, 'FAQ');

      // When user is not loggedin and when not clicked on bot pill.
      if (!isLoggedIn || (isLoggedIn && !wasChatBotClicked())) {
        dispatch(postChatBotEligibility(postChatBotParams));
      } else {
        // Logged out to logged in scenario
        dispatch(callPostChatBotEligibilityOnChatBotClick(postChatBotParams));
      }
    }

    // Post chat bot eligibility API cal for Home page
    // If current route is home.
    if (currentRouteResource === urlResource.HOME) {
      const postChatBotParams = Page.getChatBotEligibilityParams('homePage', 'GENERIC');
      if (!isLoggedIn || (isLoggedIn && !wasChatBotClicked())) {
        dispatch(postChatBotEligibility(postChatBotParams));
      } else {
        dispatch(callPostChatBotEligibilityOnChatBotClick(postChatBotParams));
      }
    }
  };
}

export default function routeDidUpdate(location) {
  return (dispatch, getState) => {
    const state = getState();
    const experience = Page.getUserExperience();
    const pageInfo = { ...location, experience };

    const pageUrlInfo = getPageUrlInfoFromLocation(pageInfo);
    const { resource } = pageUrlInfo;

    // We are calling this here in root level as we need to display the NAA alert in all pages.
    // This service will be triggered only If NAA alert is enabled for this market and popular questions state is out of sync with redux state.
    if (isLoggedIn && isNaaAlertEnabledInRC() && isPopularListOutOfSyncWithState(state)) {
      dispatch(getPopularQuestions(experience, resource.name));
    } else if (resource.name === urlResource.HOME && isPopularListOutOfSyncWithState(state)) { // Irrespective we need to fire in HOME page
      dispatch(getPopularQuestions(experience, resource.name));
    }

    if (isLocationOutOfSyncWithState(pageInfo, state)) {
      dispatch(updateRouteState(pageInfo));
      getWebAlert({ dispatch, resource });
      // Do not fire post chatbot eligibility api call on icf pages
      if (resource.name !== urlResource.PAYPALGUIDE) {
        dispatch(postChatBotEligibilityResponse(location));
      }

      const pathName = pageInfo && pageInfo.pathname;
      const icfId = Page.getIcfId(pathName);

      if (resource.name === urlResource.PAYPALGUIDE && (!icfId || icfId.indexOf(urlResource.ICF) === -1)) {
        // checking for icf id in the url and index of the icfId
        // ICF Topic page
        dispatch(getICFGuides());
      }

      if (resource.name === urlResource.PAYPALGUIDE && icfId && icfId.indexOf(urlResource.ICF) !== -1) {
        // ICF Articles page
        dispatch(fetchGuideContents(`icf_id=${icfId}`));
        dispatch(icfClicked(icfId));
      }
    }

    if (resource.name === urlResource.HOME && isPopularListOutOfSyncWithState(state)) {
      dispatch(resetToggleRelated());
    }
    if (resource.name === urlResource.HOME && isTopicTreeOutOfSyncWithState(pageInfo, state)) {
      dispatch(fetchTopicTree(experience));
      dispatch(resetToggleRelated());
    }

    if (resource.name === urlResource.TOPIC && isTopicListOutOfSyncWithState(pageInfo, state)) {
      dispatch(getArticleListByTopic(resource.id, experience));
      dispatch(resetToggleRelated());
    }

    // To update the breadcrumbs on location change.
    if (resource.name === urlResource.ARTICLE && state.article && state.article.article !== undefined) {
      dispatch(updateBreadCrumbFromArticleContent(state.article.article));
    }

    if (resource.name === urlResource.ARTICLE && isArticleSyncOutOfWithState(pageInfo, state)) {
      dispatch(fetchArticleContent(experience, resource.id));

      // Request Machine Translation localized article content
      if (Page.isMachineTranslation()) {
        dispatch(fetchMachineArticle({ faqId: resource.id, locale: Page.getMachineTranslationMarket() }));
      }
    }

    if (resource.name === urlResource.SEARCH && isSearchSyncOutOfWithState(pageInfo, state)) {
      const q = resource.searchTerm;
      let channel = Page.getChannel(resource.channelOption);

      // Update SWS channel to fire API
      if (state.searchFilters && !state.searchFilters.formUpdated && !q) {
        channel = 'product';
        // passing MTS channel, if the user not make selection, has technical experience and no channel in the url
      } else if (state.searchFilters && !state.searchFilters.formUpdated && Page.isTechnicalExp() && !resource.channelOption) {
        channel = 'mts';
      }

      dispatch(clearSuggestions());
      dispatch(fetchQueryParams({
        q,
        channel,
      }));

      if (resource.searchTerm) {
        dispatch(fetchArticleSearchResult(resource.searchTerm, experienceList[experience || 'personal'], channel));
      }
    }
  };
}
